import React, { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Form, Dimmer, Loader } from 'semantic-ui-react';
import { Controller, useFormContext } from 'react-hook-form';
import { isEqual } from 'lodash';

import { meterToAny } from 'src/utils/useful_functions';
import { SyncMetrics, SyncParams } from './SyncParamsTypes';
import './TddSyncInputs.css';

const stringifySymmetryOptions = (syncParams: SyncParams) => {
  return syncParams.options.symmetry_choices.map((choice) => ({
    key: String(choice),
    value: choice,
    text: choice ? 'Asymmetric' : 'Symmetric',
  }));
};

const stringifyLongFrameDurationOptions = (syncParams: SyncParams) => {
  return syncParams.options.long_frame_duration_choices.map((choice) => ({
    key: String(choice),
    value: choice,
    text: syncParams.options.long_frame_duration_strings[choice ? 1 : 0],
  }));
};

const stringifyChoiceWithInvalid = (choices: [number, number][]) => {
  return choices.map(([setting, invalidQty]) => ({
    key: setting,
    value: setting,
    text:
      invalidQty > 0
        ? `${setting} (${invalidQty} cannot sync)`
        : String(setting),
  }));
};

interface TddSyncInputsProps {
  syncParams: SyncParams;
  metrics?: SyncMetrics | null;
  fieldPrefix: string;
  onSettingChange?: (bandType: string, settings: any) => void;
  isSaving?: boolean;
}

export const TddSyncInputs = ({
  syncParams,
  metrics,
  fieldPrefix,
  onSettingChange,
  isSaving = false,
}: TddSyncInputsProps) => {
  const { control, watch, getValues } = useFormContext();
  const allowAsymmetric = watch(
    `${fieldPrefix}.inputs.allow_asymmetric_burst_duration`
  );
  const useAsymmetric = watch(
    `${fieldPrefix}.inputs.use_asymmetric_burst_duration`
  );

  const prefs = useSelector((state: any) => state.mainFrame.prefs);
  const permissionWrite = useSelector(
    (state: any) => state.mainFrame.permissionWrite
  );
  const { rangeUnits } = prefs;

  // Disable inputs if user only has read permission or if saving
  const disabled = !permissionWrite || isSaving;

  // Memoize computed values
  const showLongFrameDuration = useMemo(
    () => !isEqual(syncParams.options.long_frame_duration_choices, [false]),
    [syncParams.options.long_frame_duration_choices]
  );

  const symmetryOptions = useMemo(
    () => stringifySymmetryOptions(syncParams),
    [syncParams]
  );

  const longFrameOptions = useMemo(
    () => stringifyLongFrameDurationOptions(syncParams),
    [syncParams]
  );

  const burstP1Options = useMemo(
    () =>
      stringifyChoiceWithInvalid(
        syncParams.options.max_burst_duration_p1_choices
      ),
    [syncParams.options.max_burst_duration_p1_choices]
  );

  const burstP2Options = useMemo(
    () =>
      stringifyChoiceWithInvalid(
        syncParams.options.max_burst_duration_p2_choices
      ),
    [syncParams.options.max_burst_duration_p2_choices]
  );

  const frameDurationOptions = useMemo(
    () => stringifyChoiceWithInvalid(syncParams.options.frame_duration_choices),
    [syncParams.options.frame_duration_choices]
  );

  // Helper to trigger save with current form values for this band type
  const triggerSave = useCallback(
    (fieldName: string, newValue: any) => {
      if (!onSettingChange) return;

      const currentValues = getValues(`${fieldPrefix}.inputs`);
      const shortFieldName = fieldName.replace(`${fieldPrefix}.inputs.`, '');

      const settings = {
        ...currentValues,
        [shortFieldName]: newValue,
      };

      onSettingChange(fieldPrefix, settings);
    },
    [fieldPrefix, getValues, onSettingChange]
  );

  const longestPath =
    metrics?.longest_same_phase_path_length ??
    syncParams.options.longest_same_phase_path_length;
  const longestMsg =
    metrics?.longest_same_phase_message ??
    syncParams.options.longest_same_phase_message;
  const interfering =
    metrics?.number_of_interfering_paths ??
    syncParams.options.number_of_interfering_paths;

  const formattedLongestPath = meterToAny(
    longestPath,
    rangeUnits,
    3,
    false
  ).toFixed(3);
  const interferingClassName = interfering === 0 ? '' : 'invalid';

  return (
    <>
      <Dimmer.Dimmable dimmed={isSaving}>
        <Dimmer active={isSaving} inverted>
          <Loader>Calculating...</Loader>
        </Dimmer>
        <Form style={{ maxWidth: '80em' }}>
          {allowAsymmetric && (
            <Form.Group>
              <Form.Field>
                <Controller
                  key={`${fieldPrefix}.inputs.use_asymmetric_burst_duration`}
                  name={`${fieldPrefix}.inputs.use_asymmetric_burst_duration`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Form.Select
                      label="Link Symmetry"
                      options={symmetryOptions}
                      value={value}
                      disabled={disabled}
                      onChange={(_, data) => {
                        onChange(data.value);
                        triggerSave(
                          `${fieldPrefix}.inputs.use_asymmetric_burst_duration`,
                          data.value
                        );
                      }}
                    />
                  )}
                />
              </Form.Field>
              {showLongFrameDuration && (
                <Form.Field>
                  <Controller
                    key={`${fieldPrefix}.inputs.long_frame_duration`}
                    name={`${fieldPrefix}.inputs.long_frame_duration`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Form.Select
                        label="Long Frame Duration"
                        options={longFrameOptions}
                        value={value}
                        disabled={disabled}
                        onChange={(_, data) => {
                          onChange(data.value);
                          triggerSave(
                            `${fieldPrefix}.inputs.long_frame_duration`,
                            data.value
                          );
                        }}
                      />
                    )}
                  />
                </Form.Field>
              )}
            </Form.Group>
          )}
          <Form.Group>
            <Form.Field>
              <Controller
                key={`${fieldPrefix}.inputs.max_burst_duration_p1`}
                name={`${fieldPrefix}.inputs.max_burst_duration_p1`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Form.Select
                    label="Maximum Burst Duration Phase 1 (μs)"
                    options={burstP1Options}
                    value={value}
                    disabled={disabled}
                    onChange={(_, data) => {
                      onChange(data.value);
                      triggerSave(
                        `${fieldPrefix}.inputs.max_burst_duration_p1`,
                        data.value
                      );
                    }}
                  />
                )}
              />
            </Form.Field>
            {useAsymmetric && allowAsymmetric && (
              <Form.Field>
                <Controller
                  key={`${fieldPrefix}.inputs.max_burst_duration_p2`}
                  name={`${fieldPrefix}.inputs.max_burst_duration_p2`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Form.Select
                      label="Maximum Burst Duration Phase 2 (μs)"
                      options={burstP2Options}
                      value={value}
                      disabled={disabled}
                      onChange={(_, data) => {
                        onChange(data.value);
                        triggerSave(
                          `${fieldPrefix}.inputs.max_burst_duration_p2`,
                          data.value
                        );
                      }}
                    />
                  )}
                />
              </Form.Field>
            )}
            <Form.Field>
              <Controller
                key={`${fieldPrefix}.inputs.frame_duration`}
                name={`${fieldPrefix}.inputs.frame_duration`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Form.Select
                    label="Frame Duration (μs)"
                    options={frameDurationOptions}
                    value={value}
                    disabled={disabled}
                    onChange={(_, data) => {
                      onChange(data.value);
                      triggerSave(
                        `${fieldPrefix}.inputs.frame_duration`,
                        data.value
                      );
                    }}
                  />
                )}
              />
            </Form.Field>
          </Form.Group>
        </Form>
      </Dimmer.Dimmable>
      <div
        className="tdd-sync-results"
        role="region"
        aria-label="TDD Sync Results"
      >
        <div>
          <strong>Longest same-phase path:</strong> {formattedLongestPath}{' '}
          {rangeUnits}
          {longestMsg && <span> {longestMsg}</span>}
        </div>
        <div>
          <strong>Number of interfering paths:</strong>{' '}
          <span
            className={interferingClassName}
            aria-label={`${interfering} interfering paths`}
          >
            {interfering}
          </span>
        </div>
      </div>
    </>
  );
};
